import type { Ref } from 'react';
import { forwardRef } from 'react';

import classNames from 'classnames';

import type { GridTableData, GridTableHeaderItem } from '@uikit/components/GridTable';
import { Spinner } from '@uikit/components/Spinner';

import { Typography } from '@ui/uikit/components/common/Typography';

import type { BaseCampaignLeaderBoardProps } from './BaseCampaignLeaderBoard';

interface BaseCampaignLeaderBoardMobileProps<
  Data extends GridTableData = GridTableData,
  Meta = undefined,
> extends Pick<BaseCampaignLeaderBoardProps<Data, Meta>, 'columns' | 'currentUser' | 'items'> {
  showEmpty?: boolean;
  showLoader?: boolean;
  pagesLength?: number;
}

export const BaseCampaignLeaderBoardMobile = forwardRef(
  <Data extends GridTableData = GridTableData, Meta = undefined>(
    {
      columns,
      currentUser,
      items,
      showEmpty,
      showLoader,
      pagesLength,
    }: BaseCampaignLeaderBoardMobileProps<Data, Meta>,
    ref: Ref<HTMLDivElement>,
  ) => (
    <div className="overflow-y-auto min-h-[212px] max-h-[604px] w-full story-prose-none flex flex-col gap-1">
      {showEmpty && (
        <p className="text-neutral-200 rounded-xl text-sm bg-neutral-50 p-3 flex items-center justify-center flex-1">
          Leaderboard is Empty
        </p>
      )}
      {items.map((item, rowIndex) => {
        const isCurrentUser = item.user_id === currentUser.user_id;

        return (
          <div
            className={classNames('p-3 w-full rounded-xl bg-neutral-50 flex flex-col', {
              'sticky top-0 bg-primary-200 -order-1': isCurrentUser,
            })}
            key={item.user_id}
          >
            {columns.map((column, colIndex) => {
              const { name, label, render } = column;

              return (
                <div
                  key={`${item.user_id}-${label}`}
                  className={classNames(
                    'flex justify-between items-center leading-[18px] py-2 gap-4',
                    {
                      'border-b border-base-border-medium': colIndex !== columns.length - 1,
                    },
                  )}
                >
                  <Typography color="tertiary" variant="caption-s">
                    {label}
                  </Typography>
                  {render
                    ? render({
                        item,
                        rowIndex,
                        headerItem: column as unknown as GridTableHeaderItem<GridTableData, Meta>,
                        name,
                      })
                    : item[name]}
                </div>
              );
            })}
          </div>
        );
      })}
      {showLoader && (
        // Key is added to trigger in view cb again if component is already in view
        <div ref={ref} key={pagesLength} className="m-auto py-3">
          <Spinner size="md" />
        </div>
      )}
    </div>
  ),
);

BaseCampaignLeaderBoardMobile.displayName = 'BaseCampaignLeaderBoardMobile';
